/**
 * Kiosk designs made from a branch's own services.
 *
 * A template drawn once, with sample buttons, is a design every branch has to
 * rebuild: the buttons name services nobody has. So these are made at the moment
 * somebody starts a design, for one branch: a button for each service it offers,
 * laid out for the screen chosen, in one of three looks. The design is assigned to
 * that branch at once and so matches it from the start (see Designs, Assign).
 *
 * A branch offering more than fits on one page gets its first services on the
 * menu and the rest on a second page behind "Other services", with a way back —
 * which is also the plainest example of what a page is for.
 */

import type { Service } from '../lib/types'
import type { LayoutDocument, LayoutElement, LayoutText } from './types'
import { t, type StaffKey } from '../lib/staffText'

export type KioskLook = 'light' | 'dark' | 'picture'

export const KIOSK_LOOKS: Array<{ look: KioskLook; label: StaffKey }> = [
  { look: 'light', label: 'designs.look.light' },
  { look: 'dark', label: 'designs.look.dark' },
  { look: 'picture', label: 'designs.look.picture' },
]

/** Words every kiosk says, in the three languages a branch in Baku serves. */
const inAll = (key: StaffKey): LayoutText => ({ az: t(key, 'az'), ru: t(key, 'ru'), en: t(key, 'en') })
const WORDS = {
  touch: inAll('designs.kt.touch'),
  welcome: inAll('designs.kt.welcome'),
  choose: inAll('designs.kt.choose'),
  yourNumber: inAll('designs.kt.yourNumber'),
  watch: inAll('designs.kt.watch'),
  more: inAll('designs.kt.more'),
  menu: inAll('designs.ed.menu'),
} satisfies Record<string, LayoutText>

interface Look {
  theme: 'light' | 'dark'
  background: string
  button: NonNullable<LayoutElement['style']>
  heading: string
  muted: string
  back: NonNullable<LayoutElement['style']>
}

const LOOKS: Record<KioskLook, Look> = {
  light: {
    theme: 'light',
    background: '--bg-page',
    button: { background: '--bg-surface', colour: '--ink-strong', radius: 28, shadow: true, border: '--border-solid', align: 'center' },
    heading: '--ink-strong',
    muted: '--ink-muted',
    back: { background: '--bg-muted', colour: '--ink-strong', radius: 22 },
  },
  dark: {
    theme: 'dark',
    background: '#0f172a',
    button: { background: '--accent-solid', colour: '#ffffff', radius: 20, shadow: true },
    heading: '#ffffff',
    muted: '#cbd5e1',
    back: { background: '#1e293b', colour: '#ffffff', radius: 20, border: '#334155' },
  },
  picture: {
    theme: 'dark',
    background: '#10243b',
    button: { background: '#ffffff', colour: '#10243b', radius: 999, shadow: true },
    heading: '#ffffff',
    muted: '#e2e8f0',
    back: { background: '#ffffff', colour: '#10243b', radius: 999 },
  },
}

/**
 * A kiosk design for these services, on this screen, in this look. A picture
 * chosen for the picture-led look is laid behind everything; without one, its
 * deep blue stands in until a picture is added.
 */
export function kioskFromServices(
  services: readonly Service[],
  size: { width: number; height: number },
  lookName: KioskLook,
  picture?: string,
): LayoutDocument {
  const look = LOOKS[lookName]
  const { width: W, height: H } = size
  const portrait = H >= W
  const pad = Math.round(W * 0.06)
  const gap = Math.round(Math.min(W, H) * 0.03)
  const columns = portrait ? 2 : 3
  const top = Math.round(H * (portrait ? 0.22 : 0.26))
  const bottomReserve = Math.round(H * 0.14)
  const buttonH = Math.round(Math.min(H * (portrait ? 0.13 : 0.17), (W - 2 * pad) / columns * 0.62))
  const rowsThatFit = Math.max(1, Math.floor((H - top - bottomReserve + gap) / (buttonH + gap)))
  const perPage = rowsThatFit * columns

  const offered = services.filter((s) => s.active)
  // When the menu cannot hold them all, the last place on it opens the rest.
  const overflow = offered.length > perPage
  const onMenu = overflow ? offered.slice(0, perPage - 1) : offered
  const rest = overflow ? offered.slice(perPage - 1, perPage - 1 + perPage - 1) : []

  const buttonW = Math.round((W - 2 * pad - (columns - 1) * gap) / columns)
  const place = (i: number) => ({
    x: pad + (i % columns) * (buttonW + gap),
    y: top + Math.floor(i / columns) * (buttonH + gap),
    w: buttonW,
    h: buttonH,
  })
  let z = 30
  const serviceButton = (s: Service, i: number, page: string): LayoutElement => ({
    id: `svc-${page}-${s.id.slice(-8)}-${i}`,
    kind: 'button',
    ...place(i),
    z: z++,
    screens: ['choosing'],
    pages: [page],
    action: { kind: 'service', serviceId: s.id },
    style: { ...look.button },
  })

  const text = (id: string, content: LayoutText, y: number, h: number, screens: LayoutElement['screens'], colour: string, weight = 700): LayoutElement => ({
    id, kind: 'text', x: pad, y, w: W - 2 * pad, h, z: z++, screens, content,
    style: { align: 'center', weight, colour },
  })

  const elements: LayoutElement[] = []
  if (lookName === 'picture' && picture) {
    elements.push({ id: 'backdrop', kind: 'image', x: 0, y: 0, w: W, h: H, z: 0, assetId: picture, style: { align: 'center' } })
  }
  // The language switch stands top right on the welcome and the menu; the branch name is centred beside it.
  const switchW = Math.round(Math.min(W, H) * 0.24)
  const switchH = Math.round(Math.min(W, H) * 0.06)
  const side = pad + switchW + gap
  elements.push(
    { id: 'branch', kind: 'branchName', x: side, y: Math.round(H * 0.05), w: W - 2 * side, h: Math.round(H * 0.045), z: z++, style: { align: 'center', colour: look.muted } },
    {
      id: 'languages', kind: 'languages', x: W - pad - switchW, y: Math.round(H * 0.03), w: switchW, h: switchH, z: z++,
      screens: ['attract', 'choosing'], style: { align: 'right', colour: look.heading },
    },
    text('welcome', WORDS.welcome, Math.round(H * 0.3), Math.round(H * 0.08), ['attract'], look.heading),
    text('touch', WORDS.touch, Math.round(H * 0.4), Math.round(H * 0.05), ['attract'], look.muted, 500),
    text('choose', WORDS.choose, Math.round(H * (portrait ? 0.11 : 0.13)), Math.round(H * 0.07), ['choosing'], look.heading),
    text('yourNumber', WORDS.yourNumber, Math.round(H * 0.22), Math.round(H * 0.05), ['issued'], look.muted, 500),
    {
      id: 'number', kind: 'ticketNumber', x: pad, y: Math.round(H * 0.29), w: W - 2 * pad, h: Math.round(H * 0.24), z: z++,
      screens: ['issued'], style: { align: 'center', weight: 700, colour: lookName === 'light' ? '--accent-solid' : '#ffffff' },
    },
    {
      id: 'facts', kind: 'ticketFacts', x: pad, y: Math.round(H * 0.56), w: W - 2 * pad, h: Math.round(H * 0.12), z: z++,
      screens: ['issued'], style: { align: 'center', colour: look.muted },
    },
    text('watch', WORDS.watch, Math.round(H * 0.74), Math.round(H * 0.05), ['issued'], look.muted, 500),
  )
  onMenu.forEach((s, i) => elements.push(serviceButton(s, i, 'menu')))

  const pages: LayoutDocument['pages'] = [{ id: 'menu', name: WORDS.menu }]
  if (overflow) {
    pages.push({ id: 'more', name: WORDS.more })
    elements.push({
      id: 'open-more', kind: 'button', ...place(onMenu.length), z: z++,
      screens: ['choosing'], pages: ['menu'], action: { kind: 'page', pageId: 'more' },
      content: WORDS.more, style: { ...look.button },
    })
    rest.forEach((s, i) => elements.push(serviceButton(s, i, 'more')))
    const backH = Math.round(H * 0.07)
    elements.push({
      id: 'back', kind: 'button', x: pad, y: H - backH - Math.round(H * 0.04), w: Math.round(W * 0.32), h: backH, z: z++,
      screens: ['choosing'], pages: ['more'], action: { kind: 'back' }, style: { ...look.back },
    })
  }

  return {
    kind: 'kiosk',
    canvas: { width: W, height: H, theme: look.theme, background: look.background },
    pages,
    elements,
  }
}
